import { Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule, MatMenuPanel } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgTemplateOutlet } from '@angular/common';

export type WHButtonType =
  | 'flat'
  | 'stroked'
  | 'raised'
  | 'basic'
  | 'menu-item';
export type WHButtonColor = 'primary' | 'accent' | 'warn';

@Component({
  selector: 'w-h-button',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    NgTemplateOutlet,
  ],
  template: `
    <ng-template #buttonContent>
      @if (isLoading()) {
        <mat-spinner class="w-h-button-spinner" [diameter]="18" />
      }
      @if (!isLoading() && matIcon()) {
        <mat-icon>{{ matIcon() }}</mat-icon>
      }
      @if (hasText()) {
        <span class="w-h-button-text">{{ text() }}</span>
      }
    </ng-template>

    @if (isIconOnly()) {
      <button
        mat-icon-button
        [color]="hasColor() ? color() : 'accent'"
        class="w-h-button"
        [style.background-color]="buttonBackgroundColor()"
        [style.color]="buttonTextColor()"
        [disabled]="disabled() || isLoading()"
        [attr.data-cy]="dataCy() || null"
        [attr.aria-label]="matIcon()"
        [matMenuTriggerFor]="menuTriggerFor()"
        [matTooltip]="tooltip()"
        [matTooltipDisabled]="!hasTooltip()"
        (click)="handleClick()"
      >
        @if (isLoading()) {
          <mat-spinner class="w-h-button-spinner" [diameter]="18" />
        }
        @if (!isLoading() && matIcon()) {
          <mat-icon>{{ matIcon() }}</mat-icon>
        }
      </button>
    } @else {
      @switch (type()) {
        @case ('flat') {
          <button
            mat-flat-button
            [color]="hasColor() ? color() : undefined"
            [class.w-h-default-button]="!hasColor()"
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matMenuTriggerFor]="menuTriggerFor()"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
        @case ('stroked') {
          <button
            mat-stroked-button
            [color]="hasColor() ? color() : undefined"
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matMenuTriggerFor]="menuTriggerFor()"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
        @case ('raised') {
          <button
            mat-raised-button
            [color]="hasColor() ? color() : undefined"
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matMenuTriggerFor]="menuTriggerFor()"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
        @case ('basic') {
          <button
            mat-button
            [color]="hasColor() ? color() : undefined"
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matMenuTriggerFor]="menuTriggerFor()"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
        @case ('menu-item') {
          <button
            mat-menu-item
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
        @default {
          <button
            mat-stroked-button
            [color]="hasColor() ? color() : undefined"
            [class.w-h-default-button]="!hasColor()"
            class="w-h-button"
            [style.background-color]="buttonBackgroundColor()"
            [style.color]="buttonTextColor()"
            [disabled]="disabled() || isLoading()"
            [attr.data-cy]="dataCy() || null"
            [matMenuTriggerFor]="menuTriggerFor()"
            [matTooltip]="tooltip()"
            [matTooltipDisabled]="!hasTooltip()"
            (click)="handleClick()"
          >
            <ng-container *ngTemplateOutlet="buttonContent" />
          </button>
        }
      }
    }
  `,
  styles: `
    :host {
      display: inline-block;
    }

    .w-h-button-spinner {
      display: inline-block;
    }

    .w-h-button {
      line-height: 1;
    }

    .w-h-button-text {
      white-space: nowrap;
    }
  `,
})
export class WHButtonComponent {
  /** Button label text (pre-translated by the consumer) */
  readonly text = input<string>('');

  /** Material icon name (e.g. 'save', 'close', 'delete') */
  readonly matIcon = input<string>('');

  /** Shows a spinner in place of the icon and disables the button */
  readonly isLoading = input<boolean>(false);

  /** Material theme color */
  readonly color = input<WHButtonColor | undefined>(undefined);

  /** Optional custom CSS color value for button appearance (e.g. '#25882a', 'var(--success-color)') */
  readonly customColor = input<string>('');

  /** Optional custom text/icon color; if omitted, sensible defaults are applied */
  readonly customTextColor = input<string>('');

  /** Material button type */
  readonly type = input<WHButtonType>('stroked');

  /** Disables the button */
  readonly disabled = input<boolean>(false);

  /** data-cy attribute for e2e tests */
  readonly dataCy = input<string>('');

  /** Optional tooltip text */
  readonly tooltip = input<string>('');

  /** Optional menu trigger target */
  readonly menuTriggerFor = input<MatMenuPanel<any> | null>(null);

  /** Emits on click — suppressed when loading or disabled */
  readonly clicked = output<void>();

  protected readonly hasColor = computed(() => !!this.color());
  protected readonly hasCustomColor = computed(
    () => !!this.customColor()?.trim(),
  );
  protected readonly hasText = computed(() => !!this.text()?.trim());
  protected readonly hasTooltip = computed(() => !!this.tooltip()?.trim());
  protected readonly isFilledType = computed(() => {
    const currentType = this.type();
    return currentType === 'flat' || currentType === 'raised';
  });
  protected readonly isIconOnly = computed(
    () => !this.hasText() && !!this.matIcon(),
  );
  protected readonly buttonBackgroundColor = computed(() => {
    if (!this.hasCustomColor()) {
      return null;
    }

    return this.isFilledType() ? this.customColor() : null;
  });
  protected readonly buttonTextColor = computed(() => {
    if (!this.hasCustomColor()) {
      return null;
    }

    if (this.customTextColor()?.trim()) {
      return this.customTextColor();
    }

    return this.isFilledType() ? '#ffffff' : this.customColor();
  });

  protected handleClick(): void {
    if (!this.isLoading() && !this.disabled()) {
      this.clicked.emit();
    }
  }
}
